import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useFormik } from "formik";
import { useHistory, useLocation } from "react-router-dom";
import { TailSpin } from "react-loader-spinner";
import Button from "../../components/flat/ui/Button";
import { Password } from "../../components/Password";
import AuthPasswordLayout, {
  AUTH_ILLUSTRATIONS,
  ButtonText,
  Container,
  Div,
  ErrorMessage,
  Form,
  FormField,
  PanelIllustration,
  ReadOnlyInput,
  RequiredFieldLabel,
  Subtitle,
  Title,
} from "../../modules/Auth/components/AuthPasswordLayout";
import * as auth from "../../modules/Auth/_redux/authRedux";
import { passwordValidationSchema } from "../../components/Password/schema";
import { setupPassword } from "./firstAccessCrud";
import { FIRST_ACCESS_ROUTES } from "./routes";
import {
  trackPasswordCreationAttemptStarted,
  trackPasswordCreationPageDisplayed,
  trackPasswordCreationSuccessful,
} from "../../modules/Auth/trackPasswordEvents";
import * as Yup from "yup";

interface LocationState {
  email?: string;
  tempToken?: string;
}

interface PageProps {
  login: (authToken: string) => void;
}

const CreatePasswordSchema = Yup.object().shape({
  password: passwordValidationSchema.required("A nova senha é obrigatória"),
});

function CreatePasswordStep({ login }: PageProps) {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const [email, setEmail] = useState("");
  const [tempToken, setTempToken] = useState("");
  const [error, setError] = useState("");
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    trackPasswordCreationPageDisplayed();
  }, []);

  useEffect(() => {
    const fragmentParams = new URLSearchParams(location.hash.replace(/^#/, ""));
    const fragmentEmail = fragmentParams.get("email") || "";
    const fragmentToken = fragmentParams.get("token") || "";

    const queryParams = new URLSearchParams(location.search);
    const queryEmail = queryParams.get("email") || "";

    const stateEmail = location.state?.email || "";
    const stateToken = location.state?.tempToken || "";

    const resolvedEmail = stateEmail || fragmentEmail || queryEmail;
    const resolvedToken = stateToken || fragmentToken;

    if (resolvedEmail && resolvedToken) {
      setEmail(resolvedEmail);
      setTempToken(resolvedToken);
      return;
    }

    history.replace(FIRST_ACCESS_ROUTES.BASE);
  }, [location, history]);

  const form = useFormik({
    initialValues: {
      password: "",
    },
    validationSchema: CreatePasswordSchema,
    onSubmit: async values => {
      trackPasswordCreationAttemptStarted();
      setError("");
      setIsSessionExpired(false);
      setIsLoading(true);

      try {
        const { data: responseData } = await setupPassword(
          values.password,
          values.password,
          tempToken
        );

        if (
          responseData.status === "success" &&
          responseData.data?.accessToken
        ) {
          setIsSessionExpired(false);
          trackPasswordCreationSuccessful();
          login(responseData.data.accessToken);
          history.push("/");
          return;
        }

        setError(
          responseData.message ||
            "Não foi possível criar sua senha. Tente novamente."
        );
      } catch (err) {
        const status = err?.response?.status;
        const message =
          err?.response?.data?.message ||
          err?.response?.data?.msg ||
          "Não foi possível criar sua senha. Tente novamente.";

        if (status === 401) {
          setIsSessionExpired(true);
          setError(
            "Sua sessão expirou. Reinicie o fluxo de primeiro acesso para criar sua senha."
          );
          return;
        }

        setError(message);
      } finally {
        setIsLoading(false);
      }
    },
  });

  if (!email || !tempToken) {
    return null;
  }

  return (
    <AuthPasswordLayout
      rightContent={
        <PanelIllustration src={AUTH_ILLUSTRATIONS.CREATE_PASSWORD} alt="" />
      }
    >
      <Container>
        <Div>
          <Title>Criar nova senha!</Title>
          <Subtitle>
            Cadastre uma nova senha para continuar acessando a plataforma Jusfy.
          </Subtitle>
        </Div>

        <Form onSubmit={form.handleSubmit}>
          <FormField>
            <RequiredFieldLabel htmlFor="email">E-mail</RequiredFieldLabel>
            <ReadOnlyInput
              id="email"
              type="email"
              value={email}
              readOnly
              tabIndex={-1}
            />
          </FormField>

          <FormField>
            <RequiredFieldLabel htmlFor="password">Criar senha</RequiredFieldLabel>
            <Password
              id="password"
              form={form}
              showToggle
              variant="checklist"
            />
          </FormField>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          {isSessionExpired ? (
            <Button
              type="button"
              width="100%"
              padding="10px 40px"
              borderRadius="5px"
              hoverBackgroundColor="#007656"
              onClick={() => history.push(FIRST_ACCESS_ROUTES.BASE)}
            >
              <ButtonText>Reiniciar primeiro acesso</ButtonText>
            </Button>
          ) : (
            <Button
              type="submit"
              width="100%"
              padding="10px 40px"
              borderRadius="5px"
              hoverBackgroundColor="#007656"
              disabled={isLoading}
            >
              <ButtonText>
                {isLoading ? (
                  <TailSpin
                    visible
                    height="20"
                    width="20"
                    color="#ffffff"
                    ariaLabel="loading"
                  />
                ) : (
                  "Salvar senha"
                )}
              </ButtonText>
            </Button>
          )}
        </Form>
      </Container>
    </AuthPasswordLayout>
  );
}

export { CreatePasswordStep as UnconnectedCreatePasswordStep };
export default connect(null, auth.actions)(CreatePasswordStep);
